import React, {useEffect, useState} from 'react';
import {Platform, StyleSheet, View} from 'react-native';
import {BORDER_COLOR, PRIMARY_BACKGROUND_COLOR} from "../../Config/Colors";
import {CONTENT_BORDER_WIDTH, WINDOW_HEIGHT, WINDOW_WIDTH} from "../../Config/Constants";
import {
    AUTO_LOGOUT_TIMINGS_MILLISECONDS,
    HIDE_MENU_AFTER_SELECTION,
    POWER_CLIENT_USE_DUMMY_DATA,
    POWER_PROXY_USE_DUMMY_DATA, SHOW_MENU_ON_START,
    START_PAGE,
} from "../../Config/Parameters";
import SubViewTypes from "../../Domain/Types/SubViewTypes";
import PowerProxyService from "../../Domain/Services/PowerProxyService";
import PowerClientService from "../../Domain/Services/PowerClientService";
import PowerClientSubView from "../SubViews/PowerClientSubView";
import PowerProxySubView from "../SubViews/PowerProxySubView";
import MainMenu from "../MainMenu/MainMenu";
import Utils from "../../Domain/Utils/Utils";
import LoginModal from "../Shared/Modals/LoginModal";
import Logger from "../../Domain/Logger/Logger";
import {userRepository} from "../../Domain/Repositories/UserRepository";

const logger = Logger.Create("MainScreen");
let timer: NodeJS.Timeout | undefined;

/**
 * Renders the Main Screen.
 */
export default function MainScreen()
{
    const [resetTimer, setResetTimer] = useState(false);
    const [type, setType] = useState<SubViewTypes>(START_PAGE);
    const [showMenu, setShowMenu] = useState(SHOW_MENU_ON_START);
    const [showLogin, setShowLogin] = useState(false);
    
    useEffect(() =>
    {
        if (Platform.OS === 'web')
        {
            document.addEventListener("contextmenu", Utils.disableContextMenu);    
        }
        
        let isMounted = true;

        if (isMounted)
        {
            (async (): Promise<void> =>
            {
                await PowerProxyService.getProxies(POWER_PROXY_USE_DUMMY_DATA);
                await PowerClientService.getClients(POWER_CLIENT_USE_DUMMY_DATA);
            })();
        }
        
        return () => { isMounted = false };
    }, []);
    
    useEffect(() =>
    {
        if (timer !== undefined)
        {
            logger.info("Resetting timer. Re-starting the timer.");
            clearTimeout(timer);
        }
        else
        {
            logger.info("Starting timer...");
        }

        timer = setInterval(() =>
        {
            logger.info("Resetting the user details due to inactivity...");
            userRepository.removeUserDetails();
        }, AUTO_LOGOUT_TIMINGS_MILLISECONDS.duration);
        
        return () => {
            if (timer !== undefined)
            {
                clearTimeout(timer);
            }
        }
    }, [resetTimer]);
    
    const onMenuButtonPressed = (): void =>
    {
        setShowMenu(!showMenu);
    }
    
    const onMenuOptionSelected = (subViewTypes: SubViewTypes): void =>
    {
        setType(subViewTypes);
        
        if (HIDE_MENU_AFTER_SELECTION)
        {
            setShowMenu(false);
        }
    }
    
    const showSubView = (): JSX.Element =>
    {
        switch (type)
        {
            case SubViewTypes.PowerClient:
                return (
                    <PowerClientSubView
                        onMenuButtonPressed={onMenuButtonPressed}
                        onLoginPressed={() => setShowLogin(true)}
                    />
                );
            case SubViewTypes.PowerProxy:
                return (
                    <PowerProxySubView
                        onMenuButtonPressed={onMenuButtonPressed}
                        onLoginPressed={() => setShowLogin(true)}
                    />
                );
        }
    }
    
    return (
        <View
            onTouchStart={() => setResetTimer(!resetTimer)}
            style={styles.container}
        >
            {showMenu && (
                <View style={styles.menuView}>
                    <MainMenu
                        onOptionSelected={onMenuOptionSelected}
                    />
                </View>
            )}

            <View style={[styles.contentView, {
                flex: showMenu ? 0.9 : 1,
            }]}>
                {showSubView()}
            </View>
            
            <LoginModal
                show={showLogin}
                onCloseButtonSelected={() => setShowLogin(false)}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
      backgroundColor: PRIMARY_BACKGROUND_COLOR, 
      width: WINDOW_WIDTH, 
      height: WINDOW_HEIGHT, 
      alignItems: 'center', 
      justifyContent: 'center', 
      alignContent: 'center',
      flexDirection: 'row',
    },
    menuView: {
        flex: 0.2,
        height: '100%',
    },
    contentView: {
        flex: 1,
        height: '100%',
        borderWidth: CONTENT_BORDER_WIDTH,
        borderColor: BORDER_COLOR,
        paddingHorizontal: '1%',
    }
});
